// src/utils/auth.js
// Unified authentication utilities for AgentBlayna

import { STORAGE_KEYS, APP_CONFIG } from "../config";

/**
 * Parse JWT token to extract payload
 * Note: This does NOT verify the signature - that must happen server-side
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
export function parseJwt(token) {
  if (!token || typeof token !== "string") {
    return null;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Handle URL-safe base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

/**
 * Check if a token is expired or about to expire
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is expired or invalid
 */
export function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    // If no expiration claim, treat as expired for safety
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = APP_CONFIG.TOKEN_EXPIRY_BUFFER;

  return currentTime >= expirationTime - bufferTime;
}

/**
 * Get the stored authentication token
 * @returns {string|null} Token or null if not found
 */
export function getToken() {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

/**
 * Get stored user data
 * @returns {object|null} User object or null if not found
 */
export function getUser() {
  const userData = localStorage.getItem(STORAGE_KEYS.USER);
  if (!userData) return null;

  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

/**
 * Store authentication data after successful login
 * @param {object} data - Response data containing access_token and optionally user
 */
export function setAuth(data) {
  if (data.access_token) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, data.access_token);
  }
  if (data.user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
  }
}

/**
 * Clear all authentication data (logout)
 */
export function clearAuth() {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
}

/**
 * Check if user is authenticated with a valid (non-expired) token
 * @returns {boolean} True if authenticated
 */
export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;
  return !isTokenExpired(token);
}

/**
 * Get authorization headers for API requests
 * @returns {object} Headers object with Authorization if token exists
 */
export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Migrate tokens from legacy storage keys to unified key
 * Call this once on app initialization to handle existing sessions
 */
export function migrateTokens() {
  const legacyKeys = [
    "ab_token",
    "access_token",
    "accessToken",
    "token",
  ];

  // If we already have the canonical token, skip migration
  if (localStorage.getItem(STORAGE_KEYS.TOKEN)) {
    // Clean up legacy keys
    legacyKeys.forEach((key) => localStorage.removeItem(key));
    return;
  }

  // Try to find a token in legacy locations
  for (const key of legacyKeys) {
    const token = localStorage.getItem(key);
    if (token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      break;
    }
  }

  // Migrate user data
  const legacyUserKeys = ["ab_user", "agentBlayna_user"];
  if (!localStorage.getItem(STORAGE_KEYS.USER)) {
    for (const key of legacyUserKeys) {
      const user = localStorage.getItem(key);
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, user);
        break;
      }
    }
  }

  // Clean up all legacy keys
  legacyKeys.forEach((key) => localStorage.removeItem(key));
  legacyUserKeys
    .filter((k) => k !== STORAGE_KEYS.USER)
    .forEach((key) => localStorage.removeItem(key));
}
